/**
 * OPL2 FM Synthesizer for Web Audio
 *
 * Emulates the Yamaha OPL2 (YM3812) FM synthesis chip used in
 * AdLib and Sound Blaster cards. Implements 2-operator FM synthesis
 * with authentic waveforms and ADSR envelopes.
 */

import {
  OPLVoiceData,
  OPLWaveform,
  OPL_NUM_VOICES,
  decodeOPLState,
  createDefaultVoices,
} from '../shared/opl-protocol.js';

// =============================================================================
// Types
// =============================================================================

/** Single FM voice (2 operators: modulator + carrier) */
interface FMVoice {
  /** Voice ID */
  id: number;

  /** Carrier oscillator */
  carrier: OscillatorNode | null;

  /** Modulator oscillator */
  modulator: OscillatorNode | null;

  /** Carrier gain (ADSR envelope) */
  carrierGain: GainNode | null;

  /** Modulator gain (controls FM depth) */
  modulatorGain: GainNode | null;

  /** Output gain (master for this voice) */
  outputGain: GainNode | null;

  /** Currently playing state */
  voice: OPLVoiceData;

  /** Last note on state (for detecting changes) */
  wasNoteOn: boolean;
}

// =============================================================================
// OPL2 Synthesizer
// =============================================================================

/**
 * Web Audio implementation of OPL2-style FM synthesis
 */
export class OPL2Synthesizer {
  private context: AudioContext;
  private masterGain: GainNode;
  private voices: FMVoice[];
  private enabled: boolean = false;
  private masterVolume: number = 0.5;

  // Waveform lookup tables (periodic waveforms)
  private waveforms: PeriodicWave[] = [];

  constructor(audioContext: AudioContext, masterOutput?: AudioNode) {
    this.context = audioContext;

    // Create master gain
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(masterOutput || this.context.destination);

    // Initialize waveforms
    this.createWaveforms();

    // Initialize voices
    this.voices = [];
    const defaultVoices = createDefaultVoices();
    for (let i = 0; i < OPL_NUM_VOICES; i++) {
      this.voices.push({
        id: i,
        carrier: null,
        modulator: null,
        carrierGain: null,
        modulatorGain: null,
        outputGain: null,
        voice: defaultVoices[i],
        wasNoteOn: false,
      });
    }
  }

  /**
   * Create OPL2-style periodic waveforms
   */
  private createWaveforms(): void {
    const numHarmonics = 64;

    // SINE: Standard sine wave
    {
      const real = new Float32Array(numHarmonics);
      const imag = new Float32Array(numHarmonics);
      real[0] = 0;
      imag[0] = 0;
      imag[1] = 1; // Fundamental
      this.waveforms[OPLWaveform.SINE] = this.context.createPeriodicWave(real, imag);
    }

    // HALF_SINE: Only positive half of sine (like rectified)
    {
      const real = new Float32Array(numHarmonics);
      const imag = new Float32Array(numHarmonics);
      // Half-wave rectified sine has DC offset and even harmonics
      real[0] = 0.318; // DC offset
      imag[1] = 0.5;   // Fundamental (half amplitude)
      for (let n = 2; n < numHarmonics; n += 2) {
        // Even harmonics only
        real[n] = -0.212 / (n * n - 1);
      }
      this.waveforms[OPLWaveform.HALF_SINE] = this.context.createPeriodicWave(real, imag);
    }

    // ABS_SINE: Absolute value of sine (full-wave rectified)
    {
      const real = new Float32Array(numHarmonics);
      const imag = new Float32Array(numHarmonics);
      // Full-wave rectified has only even harmonics
      real[0] = 0.636; // DC offset
      for (let n = 2; n < numHarmonics; n += 2) {
        real[n] = -0.424 / (n * n - 1);
      }
      this.waveforms[OPLWaveform.ABS_SINE] = this.context.createPeriodicWave(real, imag);
    }

    // PULSE_SINE: Quarter sine then zero (pulsing sine)
    {
      const real = new Float32Array(numHarmonics);
      const imag = new Float32Array(numHarmonics);
      // This creates a "punchy" sound
      real[0] = 0.159;
      imag[1] = 0.25;
      for (let n = 2; n < numHarmonics; n++) {
        const angle = (n * Math.PI) / 4;
        real[n] = Math.cos(angle) * 0.15 / n;
        imag[n] = Math.sin(angle) * 0.15 / n;
      }
      this.waveforms[OPLWaveform.PULSE_SINE] = this.context.createPeriodicWave(real, imag);
    }
  }

  /**
   * Enable/disable the synthesizer
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (!enabled) {
      // Stop all voices
      for (const voice of this.voices) {
        this.stopVoice(voice);
      }
    }
  }

  /**
   * Check if synthesizer is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set master volume (0.0-1.0)
   */
  setVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.masterGain.gain.value = this.masterVolume;
  }

  /**
   * Get master volume
   */
  getVolume(): number {
    return this.masterVolume;
  }

  /**
   * Update voices from binary OPL state data
   */
  updateFromBinary(data: ArrayBuffer): void {
    if (!this.enabled) return;

    const { voices } = decodeOPLState(data);

    // Debug: log active voices
    const activeVoices = voices.filter(v => v.noteOn && v.amp > 0.01);
    if (activeVoices.length > 0) {
      console.log(`OPL: ${activeVoices.length} active voices`,
        activeVoices.map(v => `${v.freq.toFixed(0)}Hz@${v.amp.toFixed(2)}`).join(', '));
    }

    this.updateVoices(voices);
  }

  /**
   * Update all voices with new data
   */
  updateVoices(voiceData: OPLVoiceData[]): void {
    if (!this.enabled) return;

    for (const data of voiceData) {
      if (data.id >= 0 && data.id < this.voices.length) {
        this.updateVoice(data);
      }
    }
  }

  /**
   * Update a single voice
   */
  private updateVoice(data: OPLVoiceData): void {
    const voice = this.voices[data.id];
    const now = this.context.currentTime;

    // Check for note on/off transitions
    const noteOnTransition = data.noteOn && !voice.wasNoteOn;
    const noteOffTransition = !data.noteOn && voice.wasNoteOn;

    voice.voice = { ...data };
    voice.wasNoteOn = data.noteOn;

    if (noteOnTransition) {
      // Start new note
      this.startVoice(voice, data, now);
    } else if (noteOffTransition) {
      // Release note
      this.releaseVoice(voice, data, now);
    } else if (data.noteOn && voice.carrier) {
      // Update ongoing note (frequency/amplitude changes)
      this.updateOngoingVoice(voice, data, now);
    }
  }

  /**
   * Start a new note on a voice
   */
  private startVoice(voice: FMVoice, data: OPLVoiceData, now: number): void {
    // Stop any existing sound
    this.stopVoice(voice);

    // Create modulator oscillator
    voice.modulator = this.context.createOscillator();
    voice.modulator.setPeriodicWave(this.waveforms[data.waveform] || this.waveforms[0]);
    voice.modulator.frequency.value = data.freq * data.modRatio;

    // Create modulator gain (controls FM depth)
    voice.modulatorGain = this.context.createGain();
    voice.modulatorGain.gain.value = data.freq * data.modDepth;

    // Create carrier oscillator
    voice.carrier = this.context.createOscillator();
    voice.carrier.setPeriodicWave(this.waveforms[data.waveform] || this.waveforms[0]);
    voice.carrier.frequency.value = data.freq;

    // Create carrier gain (ADSR envelope)
    voice.carrierGain = this.context.createGain();
    voice.carrierGain.gain.value = 0;

    // Create output gain
    voice.outputGain = this.context.createGain();
    voice.outputGain.gain.value = data.amp;

    // Connect: modulator -> modulatorGain -> carrier.frequency
    voice.modulator.connect(voice.modulatorGain);
    voice.modulatorGain.connect(voice.carrier.frequency);

    // Connect: carrier -> carrierGain -> outputGain -> master
    voice.carrier.connect(voice.carrierGain);
    voice.carrierGain.connect(voice.outputGain);
    voice.outputGain.connect(this.masterGain);

    // Apply ADSR attack
    const attackEnd = now + data.attack / 1000;
    const decayEnd = attackEnd + data.decay / 1000;

    voice.carrierGain.gain.setValueAtTime(0, now);
    voice.carrierGain.gain.linearRampToValueAtTime(1, attackEnd);
    voice.carrierGain.gain.linearRampToValueAtTime(data.sustain, decayEnd);

    // Start oscillators
    voice.modulator.start(now);
    voice.carrier.start(now);
  }

  /**
   * Release a voice (start release phase)
   */
  private releaseVoice(voice: FMVoice, data: OPLVoiceData, now: number): void {
    if (!voice.carrierGain || !voice.carrier) return;

    // Cancel any scheduled values and start release
    voice.carrierGain.gain.cancelScheduledValues(now);
    voice.carrierGain.gain.setValueAtTime(voice.carrierGain.gain.value, now);

    const releaseEnd = now + data.release / 1000;
    voice.carrierGain.gain.linearRampToValueAtTime(0, releaseEnd);

    // Schedule cleanup
    const cleanupDelay = (data.release / 1000) + 0.1;
    setTimeout(() => {
      this.stopVoice(voice);
    }, cleanupDelay * 1000);
  }

  /**
   * Update an ongoing voice (frequency/amplitude changes)
   */
  private updateOngoingVoice(voice: FMVoice, data: OPLVoiceData, now: number): void {
    if (!voice.carrier || !voice.modulator || !voice.modulatorGain || !voice.outputGain) return;

    // Smooth frequency changes
    voice.carrier.frequency.setTargetAtTime(data.freq, now, 0.02);
    voice.modulator.frequency.setTargetAtTime(data.freq * data.modRatio, now, 0.02);

    // Update modulation depth
    voice.modulatorGain.gain.setTargetAtTime(data.freq * data.modDepth, now, 0.02);

    // Update output amplitude
    voice.outputGain.gain.setTargetAtTime(data.amp, now, 0.02);
  }

  /**
   * Stop a voice immediately
   */
  private stopVoice(voice: FMVoice): void {
    try {
      if (voice.carrier) {
        voice.carrier.stop();
        voice.carrier.disconnect();
      }
      if (voice.modulator) {
        voice.modulator.stop();
        voice.modulator.disconnect();
      }
      if (voice.carrierGain) {
        voice.carrierGain.disconnect();
      }
      if (voice.modulatorGain) {
        voice.modulatorGain.disconnect();
      }
      if (voice.outputGain) {
        voice.outputGain.disconnect();
      }
    } catch {
      // Ignore errors from already stopped nodes
    }

    voice.carrier = null;
    voice.modulator = null;
    voice.carrierGain = null;
    voice.modulatorGain = null;
    voice.outputGain = null;
  }

  /**
   * Stop all voices
   */
  stopAll(): void {
    for (const voice of this.voices) {
      this.stopVoice(voice);
      voice.wasNoteOn = false;
    }
  }

  /**
   * Get the number of currently active voices
   */
  getActiveVoiceCount(): number {
    return this.voices.filter(v => v.carrier !== null).length;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopAll();
    this.masterGain.disconnect();
  }
}

// =============================================================================
// Factory
// =============================================================================

/**
 * Create an OPL2Synthesizer instance
 * @param audioContext Optional AudioContext to use (creates new if not provided)
 */
export function createOPL2Synthesizer(audioContext?: AudioContext, masterOutput?: AudioNode): OPL2Synthesizer {
  const ctx = audioContext || new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  return new OPL2Synthesizer(ctx, masterOutput);
}
